import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  useTheme,
  Select,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  AccountCircle,
  Logout,
  Store as StoreIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
// Import the NotificationBell component
import NotificationBell from '../pages/notifications/NotificationBell';
import apiClient from '../../../api/client';

import useNetworkStatus from '../../../hooks/useNetworkStatus';
import { Chip } from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';


const Header = ({ handleDrawerToggle }) => {
  const isOnline = useNetworkStatus(); // Custom hook to check network status
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasLoadedRef = useRef(false);

  
  // State management
  const [anchorEl, setAnchorEl] = useState(null);
  // REMOVED: notificationAnchorEl - handled by NotificationBell
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isBranchSwitching, setIsBranchSwitching] = useState(false);
  
  const [userData, setUserData] = useState({
    name: 'User',
    email: '',
    role: '',
    image: null
  });
  const [vendorData, setVendorData] = useState({
    vendorName: '',
    branchName: '',
    logo: null
  });
  
  const MotionDiv = motion.div;
  
  // Extract and load data from JWT token - Only run once
  // Extract and load data from JWT token - Only run once
useEffect(() => {

   if (hasLoadedRef.current) {
      console.log('⚠️ Header: Data already loaded, skipping...');
      return;
    }

  const token = localStorage.getItem('token');
  if (!token) {
    navigate('/login');
    return;
  }

  hasLoadedRef.current = true;
    console.log('✅ Header: Loading data for the first time');

  try {
    const tokenData = JSON.parse(atob(token.split('.')[1]));
    
    if (tokenData.branchId) {
      setSelectedBranchId(tokenData.branchId.toString());
    }
    
    // **FIXED: Always fetch fresh vendor data on page load**
    setVendorData(prev => ({
      ...prev,
      vendorName: tokenData.vendor || 'POSlah Business', // Temporary fallback
      branchName: tokenData.branch || prev.branchName
    }));

    setUserData({
      name: tokenData.username || 'User',
      email: tokenData.email || '',
      role: tokenData.role || '',
      image: null // Initialize as null, will be loaded from API
    });
    
    // **FIXED: Always fetch fresh data on component mount**
    const fetchData = async () => {
      try {
        const [userResponse, branchesResponse, vendorResponse] = await Promise.all([
          // Fix: Use the correct endpoint that returns user details with image
          apiClient.get('/api/v1/users/detail', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          apiClient.get('/api/v1/users/list-branches', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          // **ADDED: Fetch fresh vendor data on every page load**
          apiClient.get('/api/v1/users/vendor-details', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        // Fix: Update user data with image from API response
        if (userResponse.data?.status === 'success' && userResponse.data.user) {
          const userData = userResponse.data.user;
          
          setUserData(prev => ({
            ...prev,
            name: userData.username || prev.name,
            email: userData.email || prev.email,
            // Add image processing logic similar to ProfileSettings
            image: userData.image || null
          }));
          
          console.log('User data loaded with image:', userData.image);
        }
        
        if (branchesResponse.data && Array.isArray(branchesResponse.data)) {
          setBranches(branchesResponse.data);
        }

        // **FIXED: Update vendor data with fresh data**
        if (vendorResponse.data) {
          console.log('🏢 Fresh vendor data loaded:', vendorResponse.data);
          
          let logoUrl = null;
          if (vendorResponse.data.image) {
            if (vendorResponse.data.image.includes('https://')) {
              const urlMatch = vendorResponse.data.image.match(/(https:\/\/storage\.googleapis\.com\/[^"'<>\s]+)/i);
              logoUrl = urlMatch ? urlMatch[1] : null;
            } else {
              logoUrl = `https://storage.googleapis.com/poslah-3c631.firebasestorage.app/vendor-logos/${vendorResponse.data.image}`;
            }
          }
          
          // **CRITICAL: Update with fresh vendor name**
          setVendorData(prev => ({
            ...prev,
            vendorName: vendorResponse.data.vendor_name || prev.vendorName,
            logo: logoUrl
          }));
          
          // **FIXED: Update localStorage with fresh data**
          const vendorDataForStorage = {
            ...vendorResponse.data,
            processedLogoUrl: logoUrl
          };
          localStorage.setItem('vendorDetails', JSON.stringify(vendorDataForStorage));
        }
        
      } catch (error) {
        console.error('Error fetching user/branch data:', error);
      }
    };

    fetchData();
  } catch (error) {
    console.error('Error parsing JWT token:', error);
  }
}, [navigate]);


  // Listen for vendor update events
  useEffect(() => {
    const refreshHeaderData = () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          
          setVendorData(prev => ({
            ...prev,
            vendorName: tokenData.vendor || prev.vendorName,
            branchName: tokenData.branch || prev.branchName
          }));
          
          localStorage.removeItem('vendorDetails');
          
          const fetchLogoAfterUpdate = async () => {
            try {
              const response = await apiClient.get('/api/v1/users/vendor-details', {
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Accept': 'application/json'
                }
              });
              
              if (response.data) {
                setVendorData(prev => ({
                  ...prev,
                  vendorName: response.data.vendor_name || prev.vendorName
                }));
                
                if (response.data.image) {
                  let logoUrl = null;
                  
                  if (typeof response.data.image === 'string') {
                    if (response.data.image.includes('https://')) {
                      const urlMatch = response.data.image.match(/(https:\/\/storage\.googleapis\.com\/[^"'<>\s]+)/i);
                      if (urlMatch && urlMatch[1]) {
                        logoUrl = urlMatch[1];
                      }
                    } else {
                      logoUrl = `https://storage.googleapis.com/poslah-3c631.firebasestorage.app/vendor-logos/${response.data.image}`;
                    }
                    
                    if (logoUrl) {
                      setVendorData(prev => ({
                        ...prev,
                        logo: logoUrl
                      }));
                      
                      const vendorDataForStorage = {
                        ...response.data,
                        processedLogoUrl: logoUrl
                      };
                      localStorage.setItem('vendorDetails', JSON.stringify(vendorDataForStorage));
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Error refreshing vendor data:', error);
            }
          };
          
          fetchLogoAfterUpdate();
        } catch (error) {
          console.error('Error refreshing header data:', error);
        }
      }
    };

    window.addEventListener('vendor-updated', refreshHeaderData);
    
    return () => {
      window.removeEventListener('vendor-updated', refreshHeaderData);
    };
  }, []);
  
  // Fetch logo - only when vendor name changes
  useEffect(() => {
    const fetchLogo = async () => {
      // Skip if vendor name is default or logo already exists
      if (!vendorData.vendorName || 
          vendorData.vendorName === 'POSlah Business' ||
          vendorData.logo) {
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await apiClient.get('/api/v1/users/vendor-details', {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        if (response.data) {
          let logoUrl = null;
          
          if (typeof response.data.image === 'string') {
            if (response.data.image.includes('https://')) {
              const urlMatch = response.data.image.match(/(https:\/\/storage\.googleapis\.com\/[^"'<>\s]+)/i);
              if (urlMatch && urlMatch[1]) {
                logoUrl = urlMatch[1];
              }
            } else {
              logoUrl = `https://storage.googleapis.com/poslah-3c631.firebasestorage.app/vendor-logos/${response.data.image}`;
            }
            
            if (logoUrl) {
              setVendorData(prev => ({
                ...prev,
                logo: logoUrl
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    
    fetchLogo();
  }, [vendorData.vendorName, vendorData.logo]);

  // Branch validation effect
  useEffect(() => {
    if (branches.length > 0 && selectedBranchId) {
      const branchIdString = String(selectedBranchId);
      const branchExists = branches.some(b => String(b.branch_id) === branchIdString);
      
      if (!branchExists) {
        setSelectedBranchId(String(branches[0].branch_id));
      } else if (branchIdString !== selectedBranchId) {
        setSelectedBranchId(branchIdString);
      }
    }
  }, [branches, selectedBranchId]);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleBranchChange = useCallback(async (branchId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setIsBranchSwitching(true);
      
      const response = await apiClient.post(
        '/api/v1/auth/switch-branch', 
        { newBranchId: branchId },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        
        // Parse the new token to get updated branch info
        try {
          const newTokenData = JSON.parse(atob(response.data.token.split('.')[1]));
          
          // Update local state immediately to reflect the branch change
          setSelectedBranchId(String(branchId));
          
          // Update vendor data with new branch information
          setVendorData(prev => ({
            ...prev,
            vendorName: newTokenData.vendor || prev.vendorName,
            branchName: newTokenData.branch || prev.branchName
          }));
          
        } catch (tokenError) {
          console.error('Error parsing new token:', tokenError);
        }
        
        // Clear cached data
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('vendorDetails');
        localStorage.removeItem('dashboardStats');
        
        navigate('/dashboard');
        window.dispatchEvent(new CustomEvent('branch-changed'));
      }
    } catch (error) {
      console.error('Error switching branch:', error);
      alert('Failed to switch branch. Please try again.');
    } finally {
      setIsBranchSwitching(false);
    }
  }, [navigate]);

  useEffect(() => {
    const handleVendorDataUpdate = (event) => {
      console.log('🏢 Vendor data updated in header:', event.detail);
      
      // Update vendor name immediately
      if (event.detail && event.detail.name) {
        setVendorData(prev => ({
          ...prev,
          vendorName: event.detail.name,
          logo: event.detail.logoUrl || prev.logo
        }));
        
        // Also update localStorage to persist across page refreshes
        const currentVendorData = JSON.parse(localStorage.getItem('vendorDetails') || '{}');
        const updatedVendorData = {
          ...currentVendorData,
          vendor_name: event.detail.name,
          image: event.detail.logoUrl || currentVendorData.image
        };
        localStorage.setItem('vendorDetails', JSON.stringify(updatedVendorData));
      }
    };
    // **ADD: Listen for all vendor-related events**
    window.addEventListener('vendorDataUpdated', handleVendorDataUpdate);
    window.addEventListener('vendor-updated', handleVendorDataUpdate);

    const refreshHeaderData = () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          
          setVendorData(prev => ({
            ...prev,
            vendorName: tokenData.vendor || prev.vendorName,
            branchName: tokenData.branch || prev.branchName
          }));
          
          localStorage.removeItem('vendorDetails');
          
          const fetchLogoAfterUpdate = async () => {
            try {
              const response = await apiClient.get('/api/v1/users/vendor-details', {
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Accept': 'application/json'
                }
              });
              
              if (response.data) {
                setVendorData(prev => ({
                  ...prev,
                  vendorName: response.data.vendor_name || prev.vendorName
                }));
                
                if (response.data.image) {
                  let logoUrl = null;
                  
                  if (typeof response.data.image === 'string') {
                    if (response.data.image.includes('https://')) {
                      const urlMatch = response.data.image.match(/(https:\/\/storage\.googleapis\.com\/[^"'<>\s]+)/i);
                      if (urlMatch && urlMatch[1]) {
                        logoUrl = urlMatch[1];
                      }
                    } else {
                      logoUrl = `https://storage.googleapis.com/poslah-3c631.firebasestorage.app/vendor-logos/${response.data.image}`;
                    }
                    
                    if (logoUrl) {
                      setVendorData(prev => ({
                        ...prev,
                        logo: logoUrl
                      }));
                      
                      const vendorDataForStorage = {
                        ...response.data,
                        processedLogoUrl: logoUrl
                      };
                      localStorage.setItem('vendorDetails', JSON.stringify(vendorDataForStorage));
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Error refreshing vendor data:', error);
            }
          };
          
          fetchLogoAfterUpdate();
        } catch (error) {
          console.error('Error refreshing header data:', error);
        }
      }
    };

    // Listen for both events
    window.addEventListener('vendorDataUpdated', handleVendorDataUpdate);
    window.addEventListener('vendor-updated', refreshHeaderData);
    
    // Cleanup function
    return () => {
      window.removeEventListener('vendorDataUpdated', handleVendorDataUpdate);
      window.removeEventListener('vendor-updated', refreshHeaderData);
    };
  }, []); // Empty dependency array - only run once 
  
  useEffect(() => {
  const handleProfileUpdate = () => {
    // Refresh user data when profile is updated
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.get('/api/v1/users/detail', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(response => {
        if (response.data?.status === 'success' && response.data.user) {
          const userData = response.data.user;
          
          setUserData(prev => ({
            ...prev,
            name: userData.username || prev.name,
            email: userData.email || prev.email,
            image: userData.image || null
          }));
          
          console.log('Profile updated in header:', userData.image);
        }
      })
      .catch(error => {
        console.error('Error refreshing user data:', error);
      });
    }
  };

  // Listen for profile update events
  window.addEventListener('profile-updated', handleProfileUpdate);
  window.addEventListener('user-updated', handleProfileUpdate);
  
  return () => {
    window.removeEventListener('profile-updated', handleProfileUpdate);
    window.removeEventListener('user-updated', handleProfileUpdate);
  };
}, []);
  
  const handleProfileMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);
  
  // REMOVED: handleNotificationMenuOpen and handleNotificationMenuClose

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);
  
  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    
    try {
      localStorage.clear();
      sessionStorage.clear();
      
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      
      // Navigate to thank you page instead of signin directly
      navigate('/thankyou', { replace: true });
      
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      sessionStorage.clear();
      
      try {
        navigate('/thankyou', { replace: true });
      } catch {
        window.location.href = '/thankyou';
      }
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, navigate]);

  // Memoize computed values
  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);
  // REMOVED: isNotificationMenuOpen
  const logoUrl = useMemo(() => vendorData.logo || null, [vendorData.logo]);
  const companyName = useMemo(() => vendorData.vendorName || t('company.name'), [vendorData.vendorName, t]);
  const branchName = useMemo(() => vendorData.branchName || '', [vendorData.branchName]);
  
  // Check if user can switch branches (only Admin can switch, Manager/Staff cannot)
  const canSwitchBranches = useMemo(() => {
    return userData.role === 'Admin';
  }, [userData.role]);

  const menuId = 'primary-account-menu';
  // REMOVED: notificationMenuId

  return (
    <MotionDiv
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Toolbar>

          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 40,
                height: 40,
                borderRadius: '50%',
                overflow: 'hidden'
              }}
            >
              {logoUrl ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${logoUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: '#000000',
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: '50%'
                  }}
                />
              ) : (
                <Avatar
                  sx={{ 
                    width: 40, 
                    height: 40,
                    bgcolor: '#000000',
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}
                >
                  {companyName.charAt(0)}
                </Avatar>
              )}
            </Box>
            <Box>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {companyName}
              </Typography>
              {branchName && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Select
                    value={branches.length > 0 ? selectedBranchId || '' : ''}
                    onChange={(e) => handleBranchChange(e.target.value)}
                    size="small"
                    displayEmpty
                    disabled={isBranchSwitching || !canSwitchBranches} // Disable for Manager/Staff
                    sx={{ 
                      minWidth: 120,
                      height: 24,
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': { 
                        py: 0.5,
                        pl: 1,
                        color: !canSwitchBranches ? 'text.disabled' : 'inherit'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: !canSwitchBranches ? 'action.disabled' : undefined
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: !canSwitchBranches ? 'action.disabled' : undefined
                      },
                      opacity: !canSwitchBranches ? 0.6 : 1
                    }}
                    startAdornment={
                      isBranchSwitching ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : branches.length > 0 ? (
                        <StoreIcon 
                          fontSize="small" 
                          sx={{ 
                            mr: 0.5,
                            color: !canSwitchBranches ? 'action.disabled' : 'inherit'
                          }} 
                        />
                      ) : null
                    }
                  >
                    {branches.length === 0 ? (
                      <MenuItem value="">{t('common.loading')}</MenuItem>
                    ) : (
                      branches.map(branch => (
                        <MenuItem key={branch.branch_id} value={String(branch.branch_id)}>
                          {branch.location_name}
                          {!canSwitchBranches && (
                            <Typography 
                              variant="caption" 
                              sx={{ ml: 1, color: 'text.secondary' }}
                            >
                              (Assigned)
                            </Typography>
                          )}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </Box>
              )}
            </Box>
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          {/* NETWORK INDICATOR */}
          <Chip
            icon={isOnline ? <WifiIcon style={{ color: '#4caf50' }} /> : <WifiOffIcon style={{ color: '#f44336' }} />}
            label={isOnline ? 'Online' : 'Offline'}
            color={isOnline ? 'success' : 'default'}
            variant="outlined"
            sx={{ ml: 2 }}
          />
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* CLEAN: Only NotificationBell - no hardcoded notifications */}
            <NotificationBell />
            
            <Tooltip title={t('nav.settings')}>
              <IconButton 
                color="inherit" 
                aria-label={t('nav.settings')}
                onClick={() => navigate('/dashboard/settings')}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('nav.support')}>
              <IconButton color="inherit" aria-label={t('nav.support')}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('nav.profile')}>
              <IconButton
                edge="end"
                aria-label={t('nav.profile')}
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                {userData.image ? (
                  <Avatar 
                    src={userData.image}
                    sx={{ 
                      width: 24, 
                      height: 24,
                      fontSize: '0.875rem'
                    }}
                  />
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        id={menuId}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isMenuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 220,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
        <Avatar 
          src={userData.image}
          sx={{ 
            width: 60, 
            height: 60, 
            margin: '0 auto 8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          {userData.name?.charAt(0)}
        </Avatar>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {userData.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userData.email}
          </Typography>
          <Typography variant="caption" sx={{ 
            display: 'inline-block',
            px: 1.5, 
            py: 0.5, 
            mt: 1, 
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            fontWeight: 600
          }}>
            {userData.role}
          </Typography>
        </Box>
        
        <Divider />
        
        <MenuItem 
          onClick={() => {
            handleMenuClose();
            navigate('/dashboard/profile');
          }}
        >
          <AccountCircle fontSize="small" sx={{ mr: 1.5 }} />
          {t('nav.profile')}
        </MenuItem>
        
        <MenuItem onClick={handleLogout} disabled={isLoggingOut}>
          <Logout fontSize="small" sx={{ mr: 1.5 }} />
          {isLoggingOut ? t('nav.loggingOut') || 'Logging out...' : t('nav.logout')}
        </MenuItem>
      </Menu>
      
      {/* REMOVED: The entire hardcoded notification Menu */}
    </MotionDiv>
  );
};

export default Header;